import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

const TARGET_WORD_COUNT = 1200;

export interface EnhancementProgress {
  totalModules: number;
  processedModules: number;
  successCount: number;
  errorCount: number;
  currentModule?: string;
  isComplete: boolean;
}

/**
 * Simplified client-side service that triggers server-side enhancement
 * All heavy processing is done by the bulk-enhance edge function
 */
export class AutoContentEnhancementService {
  private operationId: string | null = null;

  /**
   * Check if automatic enhancement is needed
   * Only counts modules that truly need enhancement and aren't already queued
   */
  async isMigrationNeeded(): Promise<boolean> {
    try {
      // First check: how many modules already have good content?
      const { count: enhancedCount } = await supabase
        .from('module_content')
        .select('*', { count: 'exact', head: true })
        .gte('content_word_count', 1000);

      if ((enhancedCount || 0) > 100) {
        logger.info(`✓ Content protection: ${enhancedCount} modules already enhanced`);
      }

      // Second check: how many modules need enhancement?
      const { count: shortCount, error } = await supabase
        .from('module_content')
        .select('*', { count: 'exact', head: true })
        .lt('content_word_count', 1000);

      if (error) {
        logger.error('Failed to check enhancement status:', error);
        return false;
      }

      logger.info(`📊 Modules needing enhancement (<1000 words): ${shortCount || 0}`);
      
      // Only trigger if we have significant work to do
      return (shortCount || 0) >= 50;
    } catch (error) {
      logger.error('Error checking migration status:', error);
      return false;
    }
  }

  /**
   * Get current operation status
   */
  async getOperationStatus(operationId: string) {
    const { data, error } = await supabase
      .from('content_enhancement_operations')
      .select('*')
      .eq('id', operationId)
      .single();

    if (error) {
      logger.error('Failed to get operation status:', error);
      return null;
    }

    return data;
  }

  /**
   * Trigger chunk-based server-side enhancement
   * Edge function will self-perpetuate until all modules are processed
   * Automatically resumes failed operations
   */
  async enhanceAllContent(onProgress?: (progress: EnhancementProgress) => void) {
    try {
      logger.info('🚀 Starting chunk-based enhancement system...');

      // Call bulk-enhance with 'start' action (will auto-detect and restart failed ops)
      const { data, error } = await supabase.functions.invoke('bulk-enhance', {
        body: { 
          action: 'start',
          targetWordCount: TARGET_WORD_COUNT 
        }
      });

      if (error) {
        logger.error('❌ Failed to start enhancement:', error);
        throw error;
      }

      this.operationId = data.operationId;
      
      if (data.message?.includes('restarting')) {
        logger.info(`🔄 Auto-restarting failed operation: ${this.operationId}`);
        logger.info(`📊 Remaining modules: ${data.remainingModules}`);
      } else if (data.message?.includes('Resuming')) {
        logger.info(`▶️ Resuming existing operation: ${this.operationId}`);
      } else {
        logger.info(`✅ New enhancement started: ${this.operationId}`);
        logger.info(`📊 Total modules: ${data.totalModules}`);
        logger.info(`📦 Total chunks: ${data.totalChunks}`);
      }
      
      logger.info(`💡 ${data.message}`);

      // Notify progress callback with initial state
      onProgress?.({
        totalModules: data.totalModules || data.remainingModules || 0,
        processedModules: 0,
        successCount: 0,
        errorCount: 0,
        isComplete: false
      });

      return this.operationId;
    } catch (error) {
      logger.error('❌ Failed to start enhancement:', error);
      throw error;
    }
  }

  /**
   * Cancel ongoing server-side enhancement
   */
  async abort() {
    if (!this.operationId) {
      logger.warn('⚠️ No operation to abort');
      return;
    }

    logger.info('🛑 Cancelling server-side enhancement...');
    
    // Mark operation as cancelled in database
    const { error } = await supabase
      .from('content_enhancement_operations')
      .update({
        status: 'failed',
        completed_at: new Date().toISOString(),
        error_message: 'Cancelled by user'
      })
      .eq('id', this.operationId);

    if (error) {
      logger.error('Failed to cancel operation:', error);
      throw error;
    }

    logger.info('✅ Enhancement cancelled');
    this.operationId = null;
  }

  /**
   * Get operation ID
   */
  getOperationId() {
    return this.operationId;
  }
}
